import React, { useEffect, useState } from 'react';

import { union } from 'lodash/fp';

import _ from '../locale';
import { styled } from '../theme';
import {
    AgeGroupEntity,
    CountryLocationSdi,
    DataKey,
    DataValue,
    Entity,
    SmartOptionConfig,
    SmartOptionResource,
} from '../types';
import { Color, Size, matchSize } from '../utility/common-props-helpers';
import Button from './Button';
import FlexRow from './FlexRow';
import FormikTextarea from './FormTextarea';
import H3 from './H3';
import Link from './Link';
import Modal from './Modal';
import ModalBody from './ModalBody';
import ModalButton from './ModalButton';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import { SmartOptionControl } from './index';

/**
 * Styled
 */

const Base = styled('div')<Pick<Props, 'size'>>(({ theme, size }) => ({
    minHeight: matchSize({ medium: 40, large: 50 })(size),
    fontSize: matchSize({ medium: '1.4rem', large: '1.6rem' })(size),
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'start',
    paddingTop: 18,
}));

const CancelButton = styled(Link)(({ theme }) => ({
    color: theme.color.error,
}));

const StyledText = styled('span')(({ theme }) => ({
    color: theme.color.jetBlack,
}));

/**
 * Component
 */

type Props = {
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    size?: Size;
    color?: Color;
    title?: string;
    value: DataValue;
    options: DataValue;
    granularityKey: DataKey;
    onChange: (items: DataValue) => void;
    ageGroupEntities: AgeGroupEntity[];
    countryLocationSdi: CountryLocationSdi[];
    entityHierarchies: Partial<Record<string, null | Entity[]>>;
    smartOptions: SmartOptionConfig[];
    onSmartOptionCreate?: (resource: SmartOptionResource) => void;
    onSmartOptionDelete?: (id: number) => void;
};

const CsvImportModal = React.forwardRef<any, Props>(
    (
        {
            children,
            size = 'medium',
            color = 'base',
            title,
            onChange,
            value,
            options,
            ageGroupEntities,
            entityHierarchies,
            countryLocationSdi,
            smartOptions,
            granularityKey,
            onSmartOptionCreate,
            onSmartOptionDelete,
            ...props
        },
        ref
    ) => {
        const initialText = value.join(', ');
        const [text, setText] = useState<string>(initialText);
        const [isVisible, setIsVisible] = useState(false);

        useEffect(() => {
            setText(initialText);
        }, [value]);

        const isSaveDisabled = !text.length || text === initialText;
        const buttonText = text.length ? (
            <StyledText>
                <b>{text.split(',').length}</b> {_('ids')}
            </StyledText>
        ) : (
            _('add_ids')
        );

        const handleCloseModal = (ev) => {
            setIsVisible(false);
            setText(initialText);
        };
        const handleOpenModal = (ev) => {
            setIsVisible(true);
        };

        const handleSave = (ev) => {
            ev.preventDefault();
            handleCloseModal(ev);
            onChange(text.split(','));
        };
        const handleClearClick = (ev) => {
            setText('');
            onChange([]);
        };

        const handleSmartOptionChange = (smartOptionValue: DataValue) => {
            onChange(union(value, smartOptionValue));
        };

        return (
            <Base size={size} ref={ref} {...props}>
                <FlexRow itemsSpacing={20} align="center">
                    <Button color={color} onClick={handleOpenModal}>
                        {buttonText}
                    </Button>
                    {/* @todo: move buttons out of modal */}
                    <SmartOptionControl
                        granularity={options}
                        onChange={handleSmartOptionChange}
                        smartOptions={smartOptions}
                        isCreatable
                        value={value}
                        granularityKey={granularityKey}
                        onCreate={onSmartOptionCreate}
                        onDelete={onSmartOptionDelete}
                    />
                    <CancelButton onClick={handleClearClick}>{_('clear')}</CancelButton>
                </FlexRow>

                {/* @todo: replace with SubmitModal */}
                <Modal show={isVisible} onHide={handleCloseModal} {...props}>
                    <form onSubmit={handleSave}>
                        {title && (
                            <ModalHeader closeButton>
                                <H3>{title}</H3>
                            </ModalHeader>
                        )}
                        <ModalBody>
                            <FormikTextarea
                                name={_('comma_separated_ids')}
                                rows="5"
                                value={text}
                                onChange={(ev) => {
                                    setText(ev.target.value);
                                }}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <ModalButton color="secondary" onClick={handleCloseModal} type="button">
                                {_('cancel_button')}
                            </ModalButton>
                            <ModalButton type="submit" disabled={isSaveDisabled}>
                                {_('save_button')}
                            </ModalButton>
                        </ModalFooter>
                    </form>
                </Modal>
            </Base>
        );
    }
);

export default CsvImportModal;
